
/**
 * assets/crest-ai.js
 *
 * Crest AI Device Compatibility Checker — main controller.
 *
 * This file depends on two other files being loaded first:
 *   - crest-api.js     -> gives us CrestAI.CompatibilityAPI
 *   - crest-scanner.js -> gives us CrestAI.ScannerManager
 * All three files use the `defer` attribute in the HTML, which keeps
 * them running in the order they appear on the page, so we can safely
 * assume those two classes already exist by the time this file runs.
 *
 * The only thing this file adds to the global `window` object is the
 * single function `addSubscriptionProduct`, because the project spec
 * asks for it to be reusable from outside this file. Everything else
 * lives safely inside `window.CrestAI`.
 */

(function (window, document) {
  'use strict';

  // Create (or reuse) a single namespace object so we never pollute
  // the global scope with lots of separate variables.
  const CrestAI = (window.CrestAI = window.CrestAI || {});

  // A USN (Unique Serial Number) is always 4 letters followed by 7
  // digits, e.g. "ABCD1234567" -> 11 characters total.
  const USN_LENGTH = 11;
  const USN_PATTERN = /^[A-Z]{4}[0-9]{7}$/;

  /* ==================================================================
   * InputValidator
   * A small "helper" object (not a class, because it has no state of
   * its own) that knows how to clean up and check a USN value.
   * ================================================================ */
  const InputValidator = {
    /**
     * Takes whatever the user typed (or the scanner read) and turns
     * it into a valid-shape USN candidate:
     *   - makes every letter uppercase
     *   - removes spaces and special characters
     *   - only allows letters in the first 4 characters
     *   - only allows digits after that
     *   - stops once we reach USN_LENGTH characters
     */
    sanitize(rawValue) {
      const upper = (rawValue || '').toUpperCase();
      let result = '';

      for (let i = 0; i < upper.length && result.length < USN_LENGTH; i++) {
        const char = upper[i];
        const isLetter = /[A-Z]/.test(char);
        const isDigit = /[0-9]/.test(char);

        if (result.length < 4) {
          if (isLetter) result += char;
        } else {
          if (isDigit) result += char;
        }
      }

      return result;
      // return rawValue;
    },

    // True once the user has typed all 11 characters.
    isComplete(value) {
      return (value || '').length === USN_LENGTH;
    },

    // True if the value matches the "4 letters + 7 digits" pattern.
    isValid(value) {
      return USN_PATTERN.test(value || '');
    }
  };

  CrestAI.InputValidator = InputValidator;

  /* ==================================================================
   * Status presentation map
   * Converts the backend's `status` field into a visual "tone" and
   * icon. The actual text shown to the user always comes from the
   * API's `message` field — this map only controls color/icon, never
   * the wording.
   * ================================================================ */
  const STATUS_PRESENTATION = {
    ACTIVE: { tone: 'success', icon: 'success' },
    INACTIVE: { tone: 'success', icon: 'success' },
    EXPIRED: { tone: 'success', icon: 'success' },
    BLACKLISTED: { tone: 'error', icon: 'error' },
    INVALID_SERIAL_NUMBER: { tone: 'error', icon: 'error' },
    ERROR: { tone: 'error', icon: 'error' }
  };
  const DEFAULT_PRESENTATION = { tone: 'error', icon: 'error' };

  function resolvePresentation(status) {
    const key = (status || '').toUpperCase().trim();
    // Anything we don't recognise (including the backend's free-text
    // "User not found" case) safely falls back to the error tone,
    // instead of accidentally looking like a success.
    return STATUS_PRESENTATION[key] || DEFAULT_PRESENTATION;
  }

  function parseCtaEnabled(value) {
    return value === true || value === 'true';
  }

  // Reads a boolean-ish attribute (Liquid renders `true`/`false` as
  // plain strings) and coerces it to a real boolean.
  function parseAvailable(value) {
    return value === true || value === 'true';
  }

  CrestAI.resolvePresentation = resolvePresentation;

  /* ==================================================================
   * AccordionManager
   * Opens/closes a group of [data-crest-accordion] items with a
   * smooth height animation.
   * ================================================================ */
  class AccordionManager {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-crest-accordion]'));
      this._bind();
    }

    _bind() {
      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-crest-accordion-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', () => this.toggle(item));
      });
    }

    toggle(item) {
      const isOpen = item.getAttribute('data-state') === 'open';
      if (isOpen) {
        this.close(item);
      } else {
        this.open(item);
      }
    }

    open(item) {
      const trigger = item.querySelector('[data-crest-accordion-trigger]');
      const panel = item.querySelector('[data-crest-accordion-panel]');
      if (!trigger || !panel) return;

      // Close all other accordion items
      this.items.forEach((otherItem) => {
        if (otherItem !== item && otherItem.getAttribute('data-state') === 'open') {
          this.close(otherItem);
        }
      });

      item.setAttribute('data-state', 'open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      const targetHeight = panel.scrollHeight;
      panel.style.maxHeight = '0px';
      panel.offsetHeight; // force the browser to redraw before animating
      panel.style.maxHeight = targetHeight + 'px';

      panel.addEventListener('transitionend', function handler() {
        panel.style.maxHeight = 'none';
        panel.removeEventListener('transitionend', handler);
      });
    }

    close(item) {
      const trigger = item.querySelector('[data-crest-accordion-trigger]');
      const panel = item.querySelector('[data-crest-accordion-panel]');
      if (!trigger || !panel) return;

      const currentHeight = panel.scrollHeight;
      panel.style.maxHeight = currentHeight + 'px';
      panel.offsetHeight; // force redraw
      panel.style.maxHeight = '0px';

      item.setAttribute('data-state', 'closed');
      trigger.setAttribute('aria-expanded', 'false');

      panel.addEventListener('transitionend', function handler() {
        panel.hidden = true;
        panel.removeEventListener('transitionend', handler);
      });
    }
  }

  CrestAI.AccordionManager = AccordionManager;

  /* ==================================================================
   * CartManager
   * Owns the reusable addSubscriptionProduct() flow, which talks to
   * Shopify's AJAX Cart API. The variant id is configurable (it comes
   * from a data-variant-id attribute set from the theme settings).
   * ================================================================ */
  class CartManager {
    constructor(variantId) {
      this.variantId = variantId;
    }

    /**
     * Adds the configured Crest AI subscription variant to the cart.
     * You can optionally pass a different variant id and/or line-item
     * properties (for example, to stamp the validated USN onto the
     * cart line).
     *
     * @param {{ variantId?: string|number, properties?: Object }} [overrides]
     * @returns {Promise<Object>} the parsed /cart/add.js response
     */
    addSubscriptionProduct(overrides = {}) {
      const variantId = overrides.variantId || this.variantId;

      if (!variantId) {
        return Promise.reject(
          new Error('CartManager: no variant id configured for Crest AI subscription')
        );
      }

      const payload = {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: overrides.properties || {}
          }
        ]
      };

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then((response) => {
        if (!response.ok) {
          throw new Error('CartManager: failed to add subscription product (status ' + response.status + ')');
        }
        return response.json();
      });
    }
  }

  CrestAI.CartManager = CartManager;

  /* ==================================================================
   * PopupManager
   * Handles opening/closing the popup, trapping keyboard focus inside
   * it, and dismissing it with ESC or an overlay click.
   * ================================================================ */
  class PopupManager {
    constructor(popupEl, triggerEl) {
      this.popupEl = popupEl;
      this.triggerEl = triggerEl;
      this.panelEl = popupEl.querySelector('[data-crest-popup-panel]');
      this.overlayEl = popupEl.querySelector('[data-crest-popup-overlay]');
      this.closeEl = popupEl.querySelector('[data-crest-popup-close]');
      this.lastFocused = null;

      // Bind once so we can add/remove the exact same function later.
      this._onKeydown = this._onKeydown.bind(this);
      this._bind();
    }

    _bind() {
      if (this.triggerEl) {
        this.triggerEl.addEventListener('click', () => this.open());
      }
      if (this.closeEl) {
        this.closeEl.addEventListener('click', () => this.close());
      }
      if (this.overlayEl) {
        this.overlayEl.addEventListener('click', () => this.close());
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        this._trapFocus(event);
      }
    }

    _trapFocus(event) {
      const focusable = this.panelEl.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      this.lastFocused = document.activeElement;
      this.popupEl.hidden = false;
      document.body.setAttribute('data-crest-scroll-lock', '');

      window.requestAnimationFrame(() => {
        this.popupEl.setAttribute('data-open', 'true');
      });

      document.addEventListener('keydown', this._onKeydown);

      window.requestAnimationFrame(() => {
        const firstField = this.panelEl.querySelector('[data-crest-usn-input]');
        if (firstField) firstField.focus();
      });

      this.popupEl.dispatchEvent(new CustomEvent('crest:popup:open'));
    }

    close() {
      this.popupEl.removeAttribute('data-open');
      document.body.removeAttribute('data-crest-scroll-lock');
      document.removeEventListener('keydown', this._onKeydown);

      const finish = () => {
        this.popupEl.hidden = true;
        this.panelEl.removeEventListener('transitionend', finish);
        if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
          this.lastFocused.focus();
        }
      };
      this.panelEl.addEventListener('transitionend', finish);

      this.popupEl.dispatchEvent(new CustomEvent('crest:popup:close'));
    }
  }

  CrestAI.PopupManager = PopupManager;

  /* ==================================================================
   * CrestWidget
   * Wires up one full widget instance: trigger button + popup + USN
   * input + scanner + "Add to cart" button, including the whole
   * "check compatibility" request flow.
   * ================================================================ */
  class CrestWidget {
    constructor(rootEl) {
      this.root = rootEl;
      this.widgetId = rootEl.dataset.widgetId;
      this.apiEndpoint = rootEl.dataset.apiEndpoint || '';
      this.variantId = rootEl.dataset.variantId || '';
      this.variantsById = this._buildVariantsMap();
      this.scannerEnabled = rootEl.dataset.scannerEnabled === 'true';

      // Grab every element we'll need up front.
      this.triggerBtn = rootEl.querySelector('[data-crest-popup-open]');
      this.externalTriggers = document.querySelectorAll('[data-crest-external-trigger]');
      this.popupEl = rootEl.querySelector('[data-crest-popup]');
      this.inputEl = rootEl.querySelector('[data-crest-usn-input]');
      this.fieldEl = rootEl.querySelector('[data-crest-usn-field]');
      this.statusIconEl = rootEl.querySelector('[data-crest-usn-status]');
      this.counterEl = rootEl.querySelector('[data-crest-usn-counter]');
      this.messageEl = rootEl.querySelector('[data-crest-usn-message]');
      this.ctaBtn = rootEl.querySelector('[data-crest-add-to-cart]');
      this.ctaLabelEl = rootEl.querySelector('.crest-popup__cta-label');

      this.usnViewEl = rootEl.querySelector('[data-crest-usn-view]');
      this.scannerTrigger = rootEl.querySelector('[data-crest-scanner-trigger]');
      this.scannerViewEl = rootEl.querySelector('[data-crest-scanner-view]');
      this.scannerVideoEl = rootEl.querySelector('[data-crest-scanner-video]');
      this.scannerStatusEl = rootEl.querySelector('[data-crest-scanner-status]');
      this.scannerCancelEl = rootEl.querySelector('[data-crest-scanner-cancel]');

      // If this widget's popup markup is missing required pieces, bail
      // out safely instead of throwing errors later.
      if (!this.popupEl) {
        console.warn('CrestWidget: [data-crest-popup] not found inside', rootEl);
        return;
      }

      this.api = new CrestAI.CompatibilityAPI(this.apiEndpoint);
      this.cart = new CrestAI.CartManager(this.variantId);
      this.popup = new CrestAI.PopupManager(this.popupEl, this.triggerBtn);
      this.accordions = new CrestAI.AccordionManager(this.popupEl);

      // "State" for the widget — plain values we update as things
      // change, instead of scattering variables everywhere.
      this.currentUsn = '';
      this.currentStatus = '';
      this.currentValidUntil = null;
      this.abortController = null;
      this.ctaEnabled = false;
      this.hasExistingSubscription = false;

      // Stock/availability of the current variant. This is the
      // source of truth rendered server-side via the `is-available`
      // attribute on the CTA button (see crest-popup.liquid) and kept
      // in sync on `variant:changed`. It always wins over whatever a
      // compatibility check says: if the variant is out of stock, the
      // CTA must stay disabled and labeled "Out of Stock" no matter
      // how valid the USN is.
      this.variantAvailable = this.ctaBtn ? parseAvailable(this.ctaBtn.getAttribute('is-available')) : true;

      // Text labels that can be overridden per-widget via data
      // attributes (anything not already baked into the server-side
      // markup).
      this._copy = {
        validationError: this._resolveCopy('validation-error-text', 'Please enter a valid 11-character USN'),
        apiError: this._resolveCopy('api-error-text', 'Something went wrong. Please try again.'),
        cartChecking: this._resolveCopy('cart-checking-text', 'Checking your cart…'),
        ctaDefault: this._resolveCopy('cta-label', 'Add To Cart'),
        ctaSubscribe: this._resolveCopy('cta-label-subscribe', 'Add To Cart'),
        ctaRenew: this._resolveCopy('cta-label-renew', 'Renew Now'),
        ctaOutOfStock:
          (this.ctaBtn && this.ctaBtn.getAttribute('data-out-of-stock-label')) ||
          this._resolveCopy('cta-label-out-of-stock', 'Out Of Stock')
      };

      this._bind();

      // Make sure the button's initial label/state matches the
      // variant's availability, in case the server-rendered markup
      // and our copy overrides ever disagree.
      this._applyStockState();

      if (this.scannerEnabled && this.scannerTrigger) {
        this._initScanner();
      }
    }

    // Reads a `data-*` attribute off the widget root, falling back to
    // a default string if it isn't set.
    _resolveCopy(key, fallback) {
      const attr = this.root.getAttribute('data-' + key);
      return attr || fallback;
    }

    _bind() {
      if (!this.inputEl) return;

      this.inputEl.addEventListener('input', () => this._handleInput());

      if (this.ctaBtn) {
        this.ctaBtn.addEventListener('click', () => this._handleAddToCart());
      }

      // Open the Crest AI popup from PDP Add to Cart / Buy Now buttons
      this.externalTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          this._openExternalPopup(trigger);
        });
      });

      document.addEventListener('variant:changed', (event) => this._handleVariantChanged(event));

      this.popupEl.addEventListener('crest:popup:open', () => {
        this._reset();

        this.inputEl.disabled = true;

        if (this.scannerTrigger) {
          this.scannerTrigger.disabled = true;
        }

        this._setCtaEnabled(false);

        this._checkExistingAISubscription();
      });

      // If the popup is closed mid-scan, make sure the camera is released.
      this.popupEl.addEventListener('crest:popup:close', () => {
        if (this.scanner) this.scanner.close();
        this._reset();
      });
    }

    _openExternalPopup(trigger) {
      if (!this.popup) return;

      // Remember which PDP button opened the popup.
      this.externalTrigger = trigger;

      // Open the existing Crest AI popup.
      this.popup.open();
    }

    _initScanner() {
      this.scanner = new CrestAI.ScannerManager({
        triggerEl: this.scannerTrigger,
        viewEl: this.scannerViewEl,
        videoEl: this.scannerVideoEl,
        statusEl: this.scannerStatusEl,
        cancelEl: this.scannerCancelEl,
        onOpen: () => {
          if (this.usnViewEl) this.usnViewEl.hidden = true;
        },
        onClose: () => {
          if (this.usnViewEl) this.usnViewEl.hidden = false;
        },
        onDecode: (code) => {
          this.inputEl.value = CrestAI.InputValidator.sanitize(code);
          this.inputEl.dispatchEvent(new Event('input', { bubbles: true }));
          this.inputEl.focus();
        },
        onError: (message) => {
          this._setMessage(message, 'error');
        }
      });
    }

    _handleInput() {
      const sanitized = CrestAI.InputValidator.sanitize(this.inputEl.value);

      if (this.inputEl.value !== sanitized) {
        this.inputEl.value = sanitized;
      }
      this.currentUsn = sanitized;

      this._updateCounter(sanitized.length);
      this._clearMessage();
      this._setFieldState('default');

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this._setCtaEnabled(false);
      this._setCtaLabel(this.variantAvailable ? this._copy.ctaDefault : this._copy.ctaOutOfStock);
      this.currentStatus = '';
      this.currentValidUntil = null;

      if (!CrestAI.InputValidator.isComplete(sanitized)) {
        return; // still typing — never call the API mid-entry
      }

      if (!CrestAI.InputValidator.isValid(sanitized)) {
        this._setFieldState('error');
        this._setMessage(this._copy.validationError, 'error');
        return; // client-side validation failure — no API call
      }

      // Even when the variant is out of stock, the USN still gets
      // checked against the API so the field shows a correct
      // valid/invalid status and message. `_checkCompatibility()`
      // already enforces `variantAvailable` when deciding whether to
      // enable the CTA, so the button stays locked either way.
      this._checkCompatibility(sanitized);
    }

    _checkCompatibility(usn) {
      this._setFieldState('loading');
      this.inputEl.disabled = true;
      this._setStatusIcon('spinner');
      this._clearMessage();

      this.abortController = 'AbortController' in window ? new AbortController() : null;

      this.api
        .checkCompatibility(usn, { signal: this.abortController ? this.abortController.signal : undefined })
        .then((result) => {
          // Guard against a stale/late response if the user kept
          // editing the field after this request was sent.
          if (this.currentUsn !== usn) return;

          this.inputEl.disabled = false;
          this.currentStatus = result.status;
          this.currentValidUntil = result.valid_until || null;

          const presentation = resolvePresentation(result.status);
          const ctaEnabled = parseCtaEnabled(result.cta_enabled);

          this._setFieldState(presentation.tone);
          this._setStatusIcon(presentation.icon);
          this._setMessage(result.message, presentation.tone);

          // Availability always wins: an out-of-stock variant keeps
          // the CTA disabled no matter what the API says. Next, if
          // the user already has this subscription in their cart,
          // never re-enable the CTA either.
          if (!this.variantAvailable) {
            this._setCtaEnabled(false);
            this.ctaEnabled = false;
          } else if (!this.hasExistingSubscription) {
            this._setCtaEnabled(ctaEnabled);
            this.ctaEnabled = ctaEnabled;
          }

          // Out of stock always shows "Out Of Stock", regardless of
          // compatibility status. Otherwise: INACTIVE = brand-new
          // subscription, EXPIRED = renewal — give the CTA a matching
          // label when it unlocks. Any other status falls back to the
          // default configured label.
          if (!this.variantAvailable) {
            this._setCtaLabel(this._copy.ctaOutOfStock);
          } else if (result.status === 'EXPIRED') {
            this._setCtaLabel(this._copy.ctaRenew);
          } else if (result.status === 'INACTIVE') {
            this._setCtaLabel(this._copy.ctaSubscribe);
          } else {
            this._setCtaLabel(this._copy.ctaDefault);
          }
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          if (this.currentUsn !== usn) return;

          this.inputEl.disabled = false;
          this._setFieldState('error');
          this._setStatusIcon('error');
          this._setMessage(this._copy.apiError, 'error');
          this._setCtaEnabled(false);
          this.ctaEnabled = false;
        });
    }

    _handleAddToCart() {
      if (!this.variantAvailable) return;
      if (!this.ctaEnabled || !this.ctaBtn || this.ctaBtn.disabled) return;

      this.ctaBtn.disabled = true;
      this.ctaBtn.setAttribute('data-state', 'loading');
      const metafields = this._getCurrentVariantMetafields();

      this.cart
        .addSubscriptionProduct({
          properties: {
            usn_code: this.currentUsn,
            usn_daily_limit: metafields.ai_daily_limit || '',
            usn_total_limit: metafields.ai_total_limit || '',
            duration: metafields.ai_sub_plan || ''
          }
        })
        .then(() => {
          this.popup.close();

          // Let the rest of the theme know the cart changed, using
          // whichever of these hooks actually exist on the page.
          if (typeof window.dispatch_cartUpdate === 'function') {
            window.dispatch_cartUpdate();
          }

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );

          const miniCart = document.getElementById('mini-cart');
          if (miniCart) {
            miniCart.open = true;
          }

          if (window.Shopify && typeof window.Shopify.cart_item_count === 'function') {
            window.Shopify.cart_item_count();
          }

          if (typeof window.cart_bubble === 'function') {
            window.cart_bubble();
          }
        })
        .catch(() => {
          this._setMessage(this._copy.apiError, 'error');
        })
        .finally(() => {
          this.ctaBtn.disabled = !this.ctaEnabled || !this.variantAvailable;
          this.ctaBtn.removeAttribute('data-state', 'loading');
        });
    }

    _updateCounter(length) {
      if (this.counterEl) {
        this.counterEl.textContent = length + '/' + USN_LENGTH;
      }
    }

    _setFieldState(state) {
      if (this.fieldEl) this.fieldEl.setAttribute('data-state', state);
    }

    _setStatusIcon(icon) {
      if (!this.statusIconEl) return;
      if (!icon) {
        this.statusIconEl.innerHTML = '';
        return;
      }

      // Small inline icons mirroring snippets/crest-icons.liquid,
      // kept here since these are swapped purely on the client side.
      const icons = {
        success:
          '<svg class="crest-icon crest-icon--success" viewBox="0 0 24 24" fill="none"><path d="M5 13L9.5 17.5L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        warning:
          '<svg class="crest-icon crest-icon--warning" viewBox="0 0 24 24" fill="none"><path d="M12 4L21.5 20.5H2.5L12 4Z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/><path d="M12 10.5V14.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><circle cx="12" cy="17.25" r="1" fill="currentColor"/></svg>',
        error:
          '<svg class="crest-icon crest-icon--error" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.75"/><path d="M12 7.5V13" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><circle cx="12" cy="16.25" r="1" fill="currentColor"/></svg>',
        spinner:
          '<svg class="crest-icon crest-icon--spinner" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity="0.2" stroke-width="2.25"/><path d="M21 12C21 7.02944 16.9706 3 12 3" stroke="currentColor" stroke-width="2.25" stroke-linecap="round"/></svg>'
      };
      this.statusIconEl.innerHTML = icons[icon] || '';
    }

    _formatMessage(rawText) {
      if (!rawText) return '';

      const collectionUrl = 'https://www.boat-lifestyle.com/collections/crest-ai-products';

      // Check if message is the incompatible device message (matches any variant like "not compatible", "incompatible", or collection URL)
      if (
        rawText.includes('collections/crest-ai-products') ||
        rawText.toLowerCase().includes('not compatible') ||
        rawText.toLowerCase().includes('incompatible')
      ) {
        return `Your device is not compatible with boAt AI. Find AI enabled devices <a href="${collectionUrl}">here</a>.`;
      }

      return rawText;
    }

    _setMessage(text, type) {
      if (!this.messageEl) return;
      this.messageEl.innerHTML = this._formatMessage(text || '');
      if (type) {
        this.messageEl.setAttribute('data-type', type);
      } else {
        this.messageEl.removeAttribute('data-type');
      }
    }

    _clearMessage() {
      this._setMessage('', null);
      this._setStatusIcon('');
    }

    _setCtaEnabled(enabled) {
      if (this.ctaBtn) this.ctaBtn.disabled = !enabled || !this.variantAvailable;
    }

    _setCtaLabel(label) {
      if (this.ctaLabelEl) this.ctaLabelEl.textContent = label;
    }

    // Forces the CTA back into whatever state the current
    // `variantAvailable` flag demands: locked + "Out Of Stock" when
    // unavailable, or the normal default label (still disabled until
    // a valid USN is checked) when available.
    _applyStockState() {
      if (this.ctaBtn) {
        this.ctaBtn.setAttribute('is-available', this.variantAvailable);
      }

      if (!this.variantAvailable) {
        this._setCtaEnabled(false);
        this.ctaEnabled = false;
        this._setCtaLabel(this._copy.ctaOutOfStock);
      } else {
        this._setCtaLabel(this._copy.ctaDefault);
      }
    }

    _handleVariantChanged(event) {
      if (!event.detail || !event.detail.variant) return;

      const variant = event.detail.variant;

      // Keep this widget's idea of the "current variant" up to date.
      this.variantId = variant.id;
      this.root.setAttribute('data-variant-id', variant.id);
      this.cart.variantId = variant.id;

      const wasAvailable = this.variantAvailable;
      this.variantAvailable = !!variant.available;

      // const crestPopupSubtitle = this.root.querySelector('.crest-popup__subtitle');
      // if (crestPopupSubtitle) {
      //   // crestPopupSubtitle.textContent = variant.id;
      //   const crestPopupSubtitle = this.root.querySelector('.crest-popup__subtitle');
      //   if (crestPopupSubtitle) {
      //     const price = variant.price;
      //     const plan = (variant.metafields && variant.metafields.ai_sub_plan) || '';
      //     crestPopupSubtitle.textContent = plan
      //       ? `AT JUST ₹${price} ${plan}`
      //       : `AT JUST ₹${price}`;
      //   }
      // }
      this._updateSubtitle(variant.id, variant);

      if (this.ctaBtn) {
        this.ctaBtn.setAttribute('variant-id', variant.id);
      }

      if (!this.variantAvailable) {
        // Switched to an out-of-stock variant — lock the CTA
        // immediately, regardless of any USN already checked.
        this._applyStockState();
      } else if (!wasAvailable) {
        // Switched from out-of-stock back to available. Restore the
        // default label, and if there's already a complete, validly
        // formatted USN sitting in the field, re-run the compatibility
        // check so the CTA can unlock without the user retyping it.
        this._setCtaLabel(this._copy.ctaDefault);

        if (!this.hasExistingSubscription && CrestAI.InputValidator.isValid(this.currentUsn)) {
          this._checkCompatibility(this.currentUsn);
        }
      }
    }

    // Reads the same variants JSON that <product-variants> already has
    // (rendered by product-form.liquid), keyed by variant id, so we have
    // one source of truth for price/metafields at any point in time —
    // on initial load and after variant:changed.
    _buildVariantsMap() {
      const map = {};
      const el = document.querySelector('product-variants[data-product-variants-object]');
      if (!el) return map;

      try {
        const list = JSON.parse(el.dataset.productVariantsObject);
        list.forEach((v) => {
          map[v.id] = v;
        });
      } catch (error) {
        console.warn('CrestWidget: failed to parse product variants data', error);
      }

      return map;
    }

    // Looks up the metafields for whichever variant id the widget
    // currently thinks is selected. Falls back to an empty object so
    // callers never have to null-check.
    _getCurrentVariantMetafields() {
      const variant = this.variantsById[this.variantId];
      return (variant && variant.metafields) || {};
    }

    // Updates "AT JUST ₹1799 6 Months" from the variants map, falling
    // back to whatever variant object the caller already has (e.g. the
    // one passed on variant:changed) if it isn't in the map yet.
    _updateSubtitle(variantId, fallbackVariant) {
      const subtitleEl = this.root.querySelector('.crest-popup__subtitle');
      if (!subtitleEl) return;

      // const variantData = this.variantsById[variantId] || fallbackVariant;
      // if (!variantData) return;
      const variantData = this.variantsById[variantId];
      if (!variantData) return;

      const price = variantData.price;
      const plan = (variantData.metafields && variantData.metafields.ai_sub_plan) || '';

      subtitleEl.textContent = plan ? `AT JUST ₹${price} ${plan} PLAN` : `AT JUST ₹${price}`;
    }

    // Checks whether an AI subscription product is already sitting in
    // the cart, and locks the widget down if so. Shows a spinner the
    // whole time this is in flight so the user knows something is
    // happening instead of staring at a disabled, blank input.
    _checkExistingAISubscription() {
      this._setFieldState('loading');
      this._setStatusIcon('spinner');
      this._setMessage(this._copy.cartChecking, null);

      return fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          const hasAISubscription = cart.items.some(
            (item) => item.properties && item.properties.usn_code
          );

          if (hasAISubscription) {
            this.hasExistingSubscription = true;

            this._setFieldState('error');
            this._setStatusIcon('error');
            this._setMessage(
              'AI Subscription product is already added to your cart. Please remove it before adding this product.',
              'error'
            );

            this.inputEl.disabled = true;
            if (this.scannerTrigger) {
              this.scannerTrigger.disabled = true;
            }
            this._setCtaEnabled(false);
            this.ctaEnabled = false;
          } else {
            this.hasExistingSubscription = false;

            this.inputEl.disabled = false;
            if (this.scannerTrigger) {
              this.scannerTrigger.disabled = false;
            }
            this._setFieldState('default');
            this._clearMessage();
          }
        })
        .catch(() => {
          // Ignore cart fetch failures — worst case, the "already
          // subscribed" check simply doesn't run this time. Still
          // clear the spinner so the widget isn't stuck loading.
          this._setFieldState('default');
          this._clearMessage();
        });
    }

    // Puts the widget back to its empty starting state. Called every
    // time the popup opens or closes.
    _reset() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.currentUsn = '';
      this.currentStatus = '';
      this.currentValidUntil = null;
      this.ctaEnabled = false;

      if (this.inputEl) {
        this.inputEl.value = '';
        this.inputEl.disabled = false;
      }

      this._updateCounter(0);

      this._clearMessage();
      this._setFieldState('default');
      this._setStatusIcon('');

      // Respects out-of-stock: leaves the CTA disabled + labeled
      // "Out Of Stock" instead of resetting to the default label.
      this._applyStockState();

      if (this.scannerTrigger) {
        this.scannerTrigger.disabled = false;
      }
    }
  }

  CrestAI.CrestWidget = CrestWidget;

  /* ==================================================================
   * Reusable global entry point, as required by spec:
   * window.addSubscriptionProduct(). It simply delegates to
   * CartManager so there is only one real implementation — this
   * wrapper exists purely for outside code that expects a plain
   * global function instead of a class instance.
   * ================================================================ */
  if (typeof window.addSubscriptionProduct !== 'function') {
    window.addSubscriptionProduct = function (variantId, properties) {
      const cart = new CrestAI.CartManager(variantId);
      return cart.addSubscriptionProduct({ variantId, properties });
    };
  }

  /* ==================================================================
   * Bootstrap: find and initialize every Crest AI widget on the page.
   * ================================================================ */
  function init() {
    const roots = document.querySelectorAll('[data-crest-widget]');
    roots.forEach((root) => {
      if (root.__crestWidgetInitialized) return;
      root.__crestWidgetInitialized = true;
      new CrestWidget(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);





